import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, Clock, Copy, Check } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { toast } from "sonner";
import { useLayout } from "@/context/LayoutContext";

export default function QRCodeModal({ coupon, onClose }) {
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState("");
  const { theme } = useLayout();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const updateTimeLeft = () => {
      const expiresAt = new Date(coupon.expires_at);
      setTimeLeft(
        formatDistanceToNow(expiresAt, { addSuffix: false, locale: de })
      );
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 60000);
    return () => clearInterval(interval);
  }, [coupon.expires_at]);

  const handleCopy = () => {
    navigator.clipboard.writeText(coupon.code);
    setCopied(true);
    toast.success("Code kopiert!");
    setTimeout(() => setCopied(false), 2000);
  };

  const qrValue = `${window.location.origin}/staff?code=${coupon.code}`;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="qr-modal-overlay"
    >
      <div 
        className="w-full max-w-sm animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Ticket Card */}
        <div 
          className="overflow-hidden relative rounded-3xl"
          style={{ backgroundColor: "#FFFFFF" }}
          data-testid="qr-modal-card"
        >
          {/* Ticket Notches */}
          <div 
            className="absolute w-8 h-8 rounded-full -left-4 top-1/2 -translate-y-1/2"
            style={{ backgroundColor: "#000" }}
          />
          <div 
            className="absolute w-8 h-8 rounded-full -right-4 top-1/2 -translate-y-1/2"
            style={{ backgroundColor: "#000" }}
          />

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-10 transition-opacity hover:opacity-70"
            style={{ backgroundColor: "rgba(0,0,0,0.1)" }}
            data-testid="qr-modal-close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Header */}
          <div 
            className="p-4 text-center"
            style={{ backgroundColor: theme.colors.primary, color: "#FFF" }}
          >
            <p className="text-xs uppercase tracking-widest opacity-80">
              Franky's Farm Deal
            </p>
            <h2 className="text-xl font-bold mt-1">
              {coupon.title}
            </h2>
          </div>

          {/* Dashed Line */}
          <div 
            className="mx-6"
            style={{ borderTop: "1px dashed rgba(0,0,0,0.2)" }}
          />

          {/* QR Code Section */}
          <div className="p-6 flex flex-col items-center bg-white">
            <div 
              className="p-3 rounded-xl"
              style={{ backgroundColor: "#FFFFFF", border: "4px solid #000" }}
              data-testid="qr-code-container"
            >
              <QRCodeSVG
                value={qrValue}
                size={180}
                level="H"
                includeMargin={false}
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
            </div>

            {/* Code */}
            <div className="mt-4 flex items-center gap-2">
              <code 
                className="text-2xl font-mono font-bold tracking-wider text-black"
                data-testid="coupon-code-text"
              >
                {coupon.code}
              </code>
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg transition-colors hover:opacity-70"
                style={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                data-testid="copy-code-button"
              >
                {copied ? (
                  <Check className="w-5 h-5" style={{ color: theme.colors.primary }} />
                ) : (
                  <Copy className="w-5 h-5" style={{ color: "rgba(0,0,0,0.6)" }} />
                )}
              </button>
            </div>

            {/* Expiry */}
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Gültig noch {timeLeft}</span>
            </div>
          </div>

          {/* Footer */}
          <div 
            className="p-4"
            style={{ backgroundColor: "rgba(0,0,0,0.05)", borderTop: "1px solid rgba(0,0,0,0.1)" }}
          >
            <p className="text-xs text-gray-600 text-center">
              Zeige diesen QR-Code dem Mitarbeiter zum Einlösen
            </p>
          </div>
        </div>

        {/* Close Button Below */}
        <button
          onClick={onClose}
          className="w-full mt-4 py-3 rounded-xl font-semibold transition-all hover:opacity-90"
          style={{ 
            backgroundColor: "rgba(255,255,255,0.1)",
            color: "#FFF",
            border: "1px solid rgba(255,255,255,0.2)"
          }}
          data-testid="qr-modal-close-button"
        >
          Schließen
        </button>
      </div>
    </div>
  );
}
